import { useCallback, useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';

import { apiClientFetchSynonymSearchApiKey } from '#/api/apiClientFetchSynonymSearchApiKey';
import { Box } from '#/components/Box/Box';
import { Button } from '#/components/Button/Button';
import { AutocompleteTextInputField } from '#/components/Form/AutocompleteTextInputField';
import { InputField } from '#/components/Form/InputField';
import { InfoBox } from '#/components/InfoBox/InfoBox';
import { LegacyTooltip } from '#/components/LegacyTooltip/LegacyTooltip';
import { useI18n } from '#/i18n/hooks/useI18n';
import { JobSelector } from '#/modules/salaries/components/JobSelector/JobSelector';
import { getSearchAlgoliaConfigWithApiKey } from '#/modules/search/algolia/searchAlgoliaConfig';

import { useFormContext } from './FormContext';

const sanitizeJobTitle = (value: string) =>
  value
    .toUpperCase()
    .replace(/[^A-Z\s]/g, '')
    .trim()
    .replace(/\s+/g, ' ');

const JobTitleInputContainer = styled(Box)<{ $editing?: boolean }>`
  ${(props) =>
    props.$editing &&
    `
        input {
          padding-right: 48px;
        }
      `}
`;

const description = 'Start typing to see suggestions';
const placeholder = 'Program Manager';

type Props = {
  searchApiKey: string;
};

export function JobTitleSelectorField({ searchApiKey }: Props) {
  const { i18n } = useI18n();

  // TODO: Fix this the next time the file is edited.
  // oxlint-disable-next-line typescript/no-explicit-any
  const jobTitleRef: any = useRef(null);
  // TODO: Fix this the next time the file is edited.
  // oxlint-disable-next-line typescript/no-explicit-any
  const jobTitleMatchRef: any = useRef(null);
  const { updateValues, values, clearErrors } = useFormContext();
  const [showPrompt, setShowPrompt] = useState(values.titleSuggestion);
  const [showError, setShowError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [preselected, setPreselected] = useState(!values.titleSuggestion);
  const [renderSuggestions, setRenderSuggestions] = useState(!values.jobTitle);
  const [showSelector, setShowSelector] = useState(false);
  const [showFreeTextAfter, setShowFreeTextAfter] = useState(
    Boolean(values.titleSuggestion?.length),
  );
  const [autoSuggestion, setAutoSuggestion] = useState();
  const [apiKey, setApiKey] = useState(searchApiKey);

  const focusInput = () => {
    if (jobTitleRef?.current) {
      const input: HTMLElement = jobTitleRef.current.querySelector('input');
      input.focus();
    }
  };

  const focusMatchInput = () => {
    if (jobTitleMatchRef?.current) {
      const input: HTMLElement =
        jobTitleMatchRef.current.querySelector('input');
      input.focus();
    }
  };

  useEffect(() => {
    if (showPrompt) {
      focusMatchInput();
    } else {
      setEditing(false);
      setPreselected(true);
      updateValues({
        jobTitleMatch: null,
      });
    }
  }, [showPrompt, updateValues]);
  useEffect(() => {
    if (showFreeTextAfter) {
      focusInput();
    }
  }, [showFreeTextAfter]);
  useEffect(() => {
    if (editing) {
      focusMatchInput();
    }
  }, [editing]);

  // oxlint-disable-next-line typescript/no-explicit-any
  const setSuggestion = (suggestion: any) => {
    updateValues({
      selectedSynonymId: suggestion.value.objectID,
      selectedSynonymFamilyId: suggestion.value.jobTitle.familyId,
      text: suggestion.name,
      jobTitle: suggestion.name,
      jobTitleId: suggestion.value.jobTitle.id,
      titleSuggestion: null,
    });
    setShowSelector(true);
    setAutoSuggestion(suggestion.value);
    setShowPrompt(false);
    clearErrors(['selectedSynonymId']);
  };

  // oxlint-disable-next-line typescript/no-explicit-any
  const onSuggestionSelected = (_: any, { suggestion }: any) => {
    setSuggestion(suggestion);
  };

  // oxlint-disable-next-line typescript/no-explicit-any
  const setSecondarySuggestion = (suggestion: any) => {
    updateValues({
      selectedSynonymId: suggestion.value.objectID,
      selectedSynonymFamilyId: suggestion.value.jobTitle.familyId,
      text: suggestion.name,
      jobTitleMatch: suggestion.name,
      jobTitleId: suggestion.value.jobTitle.id,
    });
    setShowSelector(true);
    setShowError(false);
    clearErrors(['selectedSynonymId']);
  };

  // oxlint-disable-next-line typescript/no-explicit-any
  const onSecondarySuggestionSelected = (_: any, { suggestion }: any) => {
    setSecondarySuggestion(suggestion);
  };

  // oxlint-disable-next-line typescript/no-explicit-any
  const setFreeText = (jobTitle: any) => {
    if (jobTitle.length) {
      setShowPrompt(true);
    }

    updateValues({
      titleSuggestion: jobTitle,
      jobTitle,
    });
  };

  // oxlint-disable-next-line typescript/no-explicit-any
  const onBlur = (e: any, suggestions: any) => {
    if (suggestions?.length) {
      if (
        sanitizeJobTitle(e.target.value) ===
        sanitizeJobTitle(suggestions[0].name)
      ) {
        setSuggestion(suggestions[0]);
      } else if (!values.jobTitleId) {
        setFreeText(e.target.value);

        if (!editing) {
          setSecondarySuggestion(suggestions[0]);
        }
      }
    } else if (!values.jobTitleId) {
      setFreeText(e.target.value);

      if (e.target.value.length) {
        setEditing(true);
        setPreselected(false);
      }
    }
  };

  // oxlint-disable-next-line typescript/no-explicit-any
  const onBlurSecondaryField = (e: any, suggestions: any) => {
    if (suggestions?.length) {
      if (
        sanitizeJobTitle(e.target.value) ===
        sanitizeJobTitle(suggestions[0].name)
      ) {
        setSecondarySuggestion(suggestions[0]);
      } else {
        if (!values.jobTitleId && suggestions.length) {
          setAutoSuggestion(suggestions[0].value);
        }

        if (!values.selectedSynonymId) {
          setShowError(true);
        }
      }
    } else {
      if (!values.jobTitleId && suggestions?.length) {
        setAutoSuggestion(suggestions[0].value);
      }

      if (!values.selectedSynonymId) {
        setShowError(true);
      }
    }
  };

  const onReset = () => {
    updateValues({
      jobTitleMatch: values.jobTitle,
      jobTitle: null,
    });
    setShowFreeTextAfter(true);
    setShowPrompt(true);
  };

  const onContinue = () => {
    updateValues({
      titleSuggestion: values.jobTitle,
    });
    setShowPrompt(true);
  };

  const onResetSecondary = () => {
    focusMatchInput();
    updateValues({
      selectedSynonymId: null,
      selectedSynonymFamilyId: null,
      jobTitleId: null,
    });
    setShowSelector(false);
  };

  const onSuggestionsUpdated = useCallback(
    // oxlint-disable-next-line typescript/no-explicit-any
    ({ suggestions, value }: any) => {
      setShowSelector(
        (suggestions.length === 0 &&
          value &&
          value.toString().trim().length >= 3) ??
          values.jobTitleId,
      );
    },
    [values.jobTitleId],
  );

  return (
    // TODO: Fix this the next time the file is edited.
    // oxlint-disable-next-line react/jsx-no-useless-fragment
    <>
      <Box mb={30}>
        {!showFreeTextAfter && (
          <div ref={jobTitleRef}>
            <AutocompleteTextInputField
              name="jobTitle"
              label={
                /* TRANSLATORS: Compensation survey */
                <Box display="flex" alignItems="center">
                  {/* TODO: Fix this the next time the file is edited. */}
                  {/* oxlint-disable-next-line react/jsx-curly-brace-presence */}
                  {'Job Title'}
                  {/* TODO: Fix this the next time the file is edited. */}
                  {/* oxlint-disable-next-line react/jsx-curly-brace-presence */}
                  {'*'}
                  <Box ml="4px">
                    <LegacyTooltip>
                      {i18n(
                        "Use the field below to see title & description recommendations that may describe your role. Don't worry about making a perfect match! Consider selecting a title that is similar to yours.",
                      )}
                    </LegacyTooltip>
                  </Box>
                </Box>
              }
              description={description}
              placeholder={placeholder}
              autocompleteType="SALARY_SURVEY_TITLES"
              // oxlint-disable-next-line typescript/no-explicit-any
              shouldRenderSuggestions={(v: any) =>
                renderSuggestions && v && v.toString().trim().length >= 3
              }
              onBlur={onBlur}
              onSuggestionSelected={onSuggestionSelected}
              onSuggestionsUpdated={
                !showPrompt ? onSuggestionsUpdated : undefined
              }
              // oxlint-disable-next-line typescript/no-explicit-any
              onChange={(e: any) => {
                if (!renderSuggestions) {
                  setRenderSuggestions(true);
                }

                if (showPrompt) {
                  updateValues({
                    titleSuggestion: e.target.value,
                    jobTitle: e.target.value,
                  });
                } else {
                  updateValues({
                    selectedSynonymId: null,
                    selectedSynonymFamilyId: null,
                    text: null,
                    jobTitle: e.target.value,
                    jobTitleId: null,
                  });
                  setShowSelector(false);
                }
              }}
              customErrorMatch={(errors) =>
                showPrompt
                  ? errors.titleSuggestion
                  : (errors.text ?? errors.selectedSynonymId)
              }
              large
              searchConfig={getSearchAlgoliaConfigWithApiKey(apiKey)}
              updateApiKey={() =>
                apiClientFetchSynonymSearchApiKey().then((newApiKey) => {
                  setApiKey(newApiKey);
                  return newApiKey;
                })
              }
            />
            {showSelector &&
              !showPrompt &&
              (values.jobTitleId ??
                (values.jobTitle &&
                  values.jobTitle.length >= 3 &&
                  !values.jobTitleId)) && (
                <Box mt={-22} minHeight={1}>
                  <JobSelector
                    id={values.jobTitleId}
                    title={values.jobTitle}
                    onSelect={({ name, id, synonymId, familyId }) => {
                      updateValues({
                        selectedSynonymId: synonymId,
                        selectedSynonymFamilyId: familyId,
                        jobTitle: name,
                        jobTitleId: id,
                      });
                    }}
                    onContinue={onContinue}
                    onReset={onReset}
                  />
                </Box>
              )}
          </div>
        )}

        {showPrompt && (
          <>
            <div
              ref={jobTitleMatchRef}
              style={{
                position: 'relative',
              }}
            >
              <AutocompleteTextInputField
                name="jobTitleMatch"
                label={showFreeTextAfter && 'Job Title*'}
                description={
                  !showFreeTextAfter && (
                    <InfoBox $boxStyle="form" $type="warning">
                      {`It looks like your job title isn't in our database—sorry about
                      that!
                    ${
                      preselected
                        ? `In order to provide the most detailed survey results,
                        we've selected a title for you.`
                        : `In order to complete the survey, we ask that you
                        still select a title below.`
                    }`}
                    </InfoBox>
                  )
                }
                placeholder={placeholder}
                autocompleteType="SALARY_SURVEY_TITLES"
                // oxlint-disable-next-line typescript/no-explicit-any
                shouldRenderSuggestions={(v: any) =>
                  v && v.toString().trim().length >= 3
                }
                onBlur={onBlurSecondaryField}
                onSuggestionSelected={onSecondarySuggestionSelected}
                onSuggestionsUpdated={onSuggestionsUpdated}
                // oxlint-disable-next-line typescript/no-explicit-any
                onChange={(e: any) => {
                  updateValues({
                    selectedSynonymId: null,
                    text: e.target.value,
                    jobTitleMatch: e.target.value,
                    jobTitleId: null,
                  });
                  setShowSelector(false);
                }}
                hasError={showError}
                customErrorMatch={(errors) =>
                  errors.text ?? errors.selectedSynonymId
                }
                large
                disabled={!editing}
                searchConfig={getSearchAlgoliaConfigWithApiKey(apiKey)}
                updateApiKey={() =>
                  apiClientFetchSynonymSearchApiKey().then((newApiKey) => {
                    setApiKey(newApiKey);
                    return newApiKey;
                  })
                }
              />
              {!editing && (
                <JobTitleInputContainer
                  position="absolute"
                  right="8px"
                  bottom={24}
                  transform="translateY(50%)"
                >
                  <Button
                    type="button"
                    variant="primary"
                    aria-label={'Edit ' + (values.text ?? 'job title')}
                    size="small"
                    onClick={() => {
                      setEditing(true);
                      setShowSelector(false);
                    }}
                    qaId="edit-title-button"
                  >
                    Edit
                  </Button>
                </JobTitleInputContainer>
              )}
            </div>
            {showSelector &&
              editing &&
              (values.jobTitleId ??
                (values.titleSuggestion &&
                  values.titleSuggestion.length >= 3 &&
                  !values.jobTitleId)) && (
                <Box mt={-22} minHeight={1}>
                  <JobSelector
                    id={values.jobTitleId}
                    title={values.jobTitleMatch}
                    onSelect={({ name, id, synonymId, familyId }) => {
                      updateValues({
                        selectedSynonymId: synonymId,
                        selectedSynonymFamilyId: familyId,
                        jobTitleMatch: name,
                        jobTitleId: id,
                      });
                    }}
                    onReset={onResetSecondary}
                    suggestion={autoSuggestion}
                  />
                </Box>
              )}
            {showFreeTextAfter && (
              <div ref={jobTitleRef}>
                <Box mt={30}>
                  <InputField
                    label="Preferred Job Title"
                    name="titleSuggestion"
                    description="Enter your actual job title here. Sorry it's not in our database yet!"
                  />
                </Box>
              </div>
            )}
          </>
        )}
      </Box>
    </>
  );
}
